import { Button } from '@components/Button/';
import DevelopmentCard from '@components/Card/DevelopmentCard';
import Block from '@components/Layout/Block';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import { getCorrectHeader, getCurrentLocale } from '@util/locale';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import { FC } from 'react';
import { ButtonPadding, Headline, NeighborhoodWrapper } from './styles';

export const contentAspectRatios = {
  1: [
    // neighborhood length
    [2 / 1, 2 / 1, 3 / 1],
  ],
  2: [
    [2 / 1, 2 / 1, 2 / 1],
    [2 / 1, 2 / 1, 1],
  ],
  3: [
    [2 / 1, 2 / 1, 3 / 1],
    [1, 1, 2 / 1],
    [1, 1, 1],
  ],
  4: [
    [2 / 1, 2 / 1, 3 / 1],
    [1, 1, 1],
    [1, 1, 1],
    [2 / 1, 2 / 1, 1],
  ],
  5: [
    [2 / 1, 2 / 1, 2 / 1],
    [1, 1, 1],
    [1, 1, 1],
    [1, 1, 1],
    [1, 1, 1],
  ],
};

export interface FeaturedNeighborhoodsProps {
  header?: string;
  neighborhoodsHeader?: string;
  viewAllNeighborhoodsButtonText?: string;
  featuredNeighborhoods?: any[];
  ctaUrl?: string;
  ctaText?: string;
  urlBase?: string;
  isShopOrShopGroupPage?: boolean;
}

// Also used for places that aren't neighborhoods, especially on regular/non-shop pages
const FeaturedNeighborhoods: FC<FeaturedNeighborhoodsProps> = observer(
  ({
    header,
    neighborhoodsHeader,
    viewAllNeighborhoodsButtonText,
    featuredNeighborhoods, // this accepts the whole neighborhood object - also used for non-neighborhood places
    ctaUrl,
    ctaText,
    urlBase,
    isShopOrShopGroupPage = false, // This check allows us to pull in a different slug from neighborhood pages
  }) => {
    const t = useTranslations();
    const locale = getCurrentLocale();
    const { shopStore } = useStores();
    const { shopPathPrefix } = shopStore;

    const featuredNeighborhoodsHeaderProcessed = getCorrectHeader(
      { headerData: neighborhoodsHeader ?? '' },
      locale,
    );

    const featuredNeighborhoodsButtonTextProcessed = getCorrectHeader(
      { headerData: viewAllNeighborhoodsButtonText ?? '' },
      locale,
    );

    const viewAllUrl = `${shopPathPrefix}${ctaUrl ?? '/our-neighborhoods'}`;

    return (
      <Block>
        <Headline data-testid="featured-neighborhoods-header">
          {header ?? featuredNeighborhoodsHeaderProcessed ?? t('Featured Neighborhoods')}
        </Headline>
        <VerticalLineSeparator />
        {featuredNeighborhoods && featuredNeighborhoods.length > 0 && (
          <NeighborhoodWrapper
            data-testid="featured-neighborhoods-wrapper"
            $amount={featuredNeighborhoods.length}
          >
            {featuredNeighborhoods.map((neighborhood: any, i: number) => {
              const url = urlBase || '';
              return (
                <div key={neighborhood.sys?.id} data-testid={`featured-neighborhoods-card-${i}`}>
                  <DevelopmentCard
                    img={neighborhood.fields?.featuredImg}
                    title={neighborhood.fields?.name}
                    url={
                      neighborhood.fields?.slug
                        ? isShopOrShopGroupPage
                          ? `${url}/our-neighborhoods/${neighborhood.fields?.slug}`
                          : `${url}/featured-areas/${neighborhood.fields?.slug}`
                        : null
                    }
                    imageAspectRatio={contentAspectRatios[featuredNeighborhoods.length][i]}
                  />
                </div>
              );
            })}
          </NeighborhoodWrapper>
        )}
        {featuredNeighborhoods && featuredNeighborhoods.length > 2 && (
          <ButtonPadding>
            <Button
              dataTestId="view-all-neighborhoods-btn"
              variant="secondary"
              label={
                ctaText ?? featuredNeighborhoodsButtonTextProcessed ?? t('View all neighborhoods')
              }
              url={viewAllUrl}
              arrow={false}
            />
          </ButtonPadding>
        )}
      </Block>
    );
  },
);

export default FeaturedNeighborhoods;
